// Each owner's own looks row on the web (TOM-595, cloud ADR 0364): the
// named panels `GET /v1/boats/:boatId/looks` sends, its live frame, and the
// undo of the last change, which Neo's receipt offers. Private looks are
// retired (cloud ADR 0461, TOM-962): the row's `looks` is always empty.
//
// Pure, apart from the undo request. The undo announces the chat line it
// wrote, when it took back Neo's change (TOM-662, neoUndoNote.ts). Decoded
// defensively: a named panel that is not one is dropped, never listed
// half-filled.

import {
  NEO_BOARD_LOOK_SCOPES,
  type NeoBoardLook,
  type NeoBoardLookMatch,
  type NeoBoardLooksBoard,
  type NeoBoardLooksState,
  type NeoBoardLooksUndo,
  type NeoJson,
  type NeoNamedControl,
} from '@neomaris/protocol';

import { api } from '../api';
import { announceUndoChatNote } from './neoUndoNote';

/**
 * Fired on `window` with `{ boatId }` when this page changed the owner's
 * looks (a receipt's Undo), so an open board
 * reads them again even before the socket's `looks` frame arrives.
 */
export const NEO_LOOKS_UPDATED_EVENT = 'nm:neo-looks-updated';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function revisionOf(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
}

function optionsOf(v: unknown): Record<string, NeoJson> {
  return isRecord(v) ? (v as Record<string, NeoJson>) : {};
}

function matchOf(v: unknown): NeoBoardLookMatch | null {
  if (!isRecord(v)) return null;
  const match: NeoBoardLookMatch = {};
  for (const key of ['item', 'control', 'family', 'reading'] as const) {
    const value = stringOf(v[key]);
    if (value !== undefined) match[key] = value;
  }
  return Object.keys(match).length === 0 ? null : match;
}

function lookOf(raw: unknown): NeoBoardLook | null {
  if (!isRecord(raw)) return null;
  const id = stringOf(raw.id);
  const scope = (NEO_BOARD_LOOK_SCOPES as readonly unknown[]).includes(raw.scope)
    ? (raw.scope as NeoBoardLook['scope'])
    : undefined;
  const match = matchOf(raw.match);
  if (id === undefined || scope === undefined || match === null) return null;
  const board = stringOf(raw.board) ?? null;
  // A board or item look names its board; a boat look names none.
  if ((scope === 'boat') !== (board === null)) return null;
  const panel = stringOf(raw.panel);
  return {
    id,
    scope,
    board,
    match,
    ...(panel === undefined ? {} : { panel }),
    options: optionsOf(raw.options),
    except: Array.isArray(raw.except)
      ? raw.except.filter((e): e is string => typeof e === 'string')
      : [],
    at: stringOf(raw.at) ?? '',
  };
}

function namedOf(raw: unknown): NeoNamedControl | null {
  if (!isRecord(raw)) return null;
  const id = stringOf(raw.id);
  const name = stringOf(raw.name);
  const control = stringOf(raw.control);
  if (id === undefined || name === undefined || control === undefined) return null;
  const bindings: Record<string, string> = {};
  if (isRecord(raw.bindings)) {
    for (const [role, path] of Object.entries(raw.bindings)) {
      if (typeof path === 'string' && path.length > 0) bindings[role] = path;
    }
  }
  const presetId = stringOf(raw.presetId);
  return {
    id,
    name,
    control,
    bindings,
    ...(presetId === undefined ? {} : { presetId }),
    options: optionsOf(raw.options),
    at: stringOf(raw.at) ?? '',
  };
}

function undoOf(raw: unknown): NeoBoardLooksUndo | null {
  if (!isRecord(raw)) return null;
  const revision = revisionOf(raw.revision);
  const at = stringOf(raw.at);
  if (revision === null || at === undefined) return null;
  if (raw.by !== 'owner' && raw.by !== 'neo') return null;
  return { revision, at, turnRef: stringOf(raw.turnRef) ?? null, by: raw.by };
}

/** The owner's looks for a boat, as the route sends them, or null when the body is not that. */
export function parseBoardLooksState(raw: unknown): NeoBoardLooksState | null {
  if (!isRecord(raw)) return null;
  const revision = revisionOf(raw.revision);
  if (revision === null) return null;
  const list = <T>(v: unknown, of: (r: unknown) => T | null): T[] =>
    (Array.isArray(v) ? v : []).map(of).filter((x): x is T => x !== null);
  const boards = list<NeoBoardLooksBoard>(raw.boards, (b) => {
    if (!isRecord(b)) return null;
    const id = stringOf(b.id);
    const slug = stringOf(b.slug);
    const title = stringOf(b.title);
    return id === undefined || slug === undefined || title === undefined
      ? null
      : { id, slug, title };
  });
  return {
    revision,
    looks: list(raw.looks, lookOf),
    named: list(raw.named, namedOf),
    undo: undoOf(raw.undo),
    boards,
  };
}

/**
 * A `looks` frame on the owner's own socket (api_contracts §8, TOM-592): the
 * owner's looks for `boatId` are now at `revision`. Null for any other frame.
 */
export function parseLooksChangedFrame(
  frame: unknown
): { boatId: string; revision: number } | null {
  if (!isRecord(frame) || frame.type !== 'looks' || !isRecord(frame.payload)) return null;
  const boatId = stringOf(frame.payload.boatId);
  const revision = revisionOf(frame.payload.revision);
  return boatId === undefined || revision === null ? null : { boatId, revision };
}

/** Each option field, in plain words, for what a look changes and for the chip. */
const FIELD_WORDS: Readonly<Record<string, string>> = {
  panel: 'kind of panel',
  // A page's `mine` (TOM-619, TOM-620): the owner's own CSS follows the page's.
  css: 'style',
  variant: 'Detailed or Simple',
  accent: 'accent colour',
  decimals: 'decimal places',
  historyWindow: 'history window',
  scaleMode: 'scale',
  scaleMin: 'scale',
  scaleMax: 'scale',
  pointerStyle: 'pointer',
  pointer: 'pointer',
  animationDuration: 'animation',
  surface: 'background',
  showSource: 'source line',
  showAge: 'age line',
};

/** One option field in plain words: "accent colour", "line width". */
export function fieldWord(field: string): string {
  const known = FIELD_WORDS[field];
  if (known !== undefined) return known;
  return field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
}

// ----- Remove and Undo ------------------------------------------------------------------

/**
 * How a change to the looks went. `conflict`: they changed on another screen
 * since this one read them, so nothing was written. `missing`: what was asked
 * for isn't there any more (a removed look, no step to undo). Both carry the
 * looks as they are when the server sent them.
 */
export type LooksWriteOutcome =
  | { kind: 'done'; state: NeoBoardLooksState }
  | { kind: 'conflict'; state: NeoBoardLooksState | null }
  | { kind: 'missing'; state: NeoBoardLooksState | null }
  | { kind: 'failed' };

function statusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

function currentOf(err: unknown): NeoBoardLooksState | null {
  const body = (err as { body?: unknown }).body;
  return isRecord(body) ? parseBoardLooksState(body.current) : null;
}

/**
 * Undo the last change to the owner's looks: their own, or Neo's whole last
 * request. `revision` is the revision this screen saw; a change since is
 * `conflict` (`409 changed_since`), and no step left is `missing`
 * (`404 nothing_to_undo`).
 */
export async function undoBoardLooks(
  boatId: string,
  revision: number,
  idempotencyKey: string
): Promise<LooksWriteOutcome> {
  try {
    const body = await api<unknown>(`/v1/boats/${boatId}/looks/undo`, {
      method: 'POST',
      body: { revision },
      idempotencyKey,
    });
    announceUndoChatNote(body);
    const state = parseBoardLooksState(body);
    return state === null ? { kind: 'failed' } : { kind: 'done', state };
  } catch (err) {
    const status = statusOf(err);
    if (status === 409) return { kind: 'conflict', state: currentOf(err) };
    if (status === 404) return { kind: 'missing', state: currentOf(err) };
    return { kind: 'failed' };
  }
}

/** Tell this page's open boards that the boat's looks changed. */
export function announceLooksChanged(boatId: string): void {
  window.dispatchEvent(new CustomEvent(NEO_LOOKS_UPDATED_EVENT, { detail: { boatId } }));
}

// ----- Named panels by hand (TOM-1047) ---------------------------------------------------

/**
 * How saving, renaming or removing a named panel by hand went: `refused`
 * carries the server's plain reason (a name taken, a name that cannot be
 * kept, a chart that is no panel); the rest are as for Undo.
 */
export type NamedWriteOutcome =
  | LooksWriteOutcome
  | { kind: 'refused'; error: string; message: string };

function refusalOf(err: unknown): { error: string; message: string } | null {
  const body = (err as { body?: unknown }).body;
  if (!isRecord(body)) return null;
  const error = stringOf(body.error);
  return error === undefined ? null : { error, message: stringOf(body.message) ?? error };
}

/** The owner's looks for a boat, or null when they could not be read. */
export async function loadBoardLooks(boatId: string): Promise<NeoBoardLooksState | null> {
  try {
    return parseBoardLooksState(await api<unknown>(`/v1/boats/${boatId}/looks`));
  } catch {
    return null;
  }
}

async function namedWrite(
  url: string,
  init: { method: 'POST' | 'PATCH' | 'DELETE'; body?: unknown; idempotencyKey: string }
): Promise<NamedWriteOutcome> {
  try {
    const state = parseBoardLooksState(await api<unknown>(url, init));
    return state === null ? { kind: 'failed' } : { kind: 'done', state };
  } catch (err) {
    const status = statusOf(err);
    if (status === 409) return { kind: 'conflict', state: currentOf(err) };
    const refusal = refusalOf(err);
    if (status === 404 && refusal?.error === 'named_not_found') {
      return { kind: 'missing', state: currentOf(err) };
    }
    if ((status === 422 || status === 404) && refusal !== null) {
      return { kind: 'refused', ...refusal };
    }
    return { kind: 'failed' };
  }
}

/**
 * Save one panel, instrument or page on a board as the owner's own named
 * panel, as it is: `POST /v1/boats/:boatId/looks/named`.
 */
export function saveNamedPanel(
  boatId: string,
  from: { revision: number; name: string; board: string; itemId: string },
  idempotencyKey: string
): Promise<NamedWriteOutcome> {
  return namedWrite(`/v1/boats/${boatId}/looks/named`, {
    method: 'POST',
    body: {
      expectedRevision: from.revision,
      name: from.name,
      board: from.board,
      itemId: from.itemId,
    },
    idempotencyKey,
  });
}

/** Rename one named panel: `PATCH /v1/boats/:boatId/looks/named/:namedId`. */
export function renameNamedPanel(
  boatId: string,
  namedId: string,
  revision: number,
  name: string,
  idempotencyKey: string
): Promise<NamedWriteOutcome> {
  return namedWrite(`/v1/boats/${boatId}/looks/named/${namedId}`, {
    method: 'PATCH',
    body: { expectedRevision: revision, name },
    idempotencyKey,
  });
}

/** Remove one named panel: its placements stay on their boards. */
export function removeNamedPanel(
  boatId: string,
  namedId: string,
  revision: number,
  idempotencyKey: string
): Promise<NamedWriteOutcome> {
  return namedWrite(`/v1/boats/${boatId}/looks/named/${namedId}?expectedRevision=${revision}`, {
    method: 'DELETE',
    idempotencyKey,
  });
}
